import { legacyLocalLabel } from '../../../../utilities/legacyDatePresentation';
import { startOfDay } from 'date-fns';
import { parseLocalDate as parseLocalPattern } from '../../../../utilities/dateTime';
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { AlertCircle, ArrowRight, Calendar, Clock, FileKey, Loader2, X } from "lucide-react";
import { toast } from "react-toastify";

import { useAuth } from "../../../../firebase/AuthProvider";
import { UpdateLeaveRequestTypeDto } from "../../../../interfaces/Employee/Employee";
import { LeaveBalance, LeaveRequestBalancePreview } from "../../../../interfaces/leave/leaveManagement";
import { LeaveManagementService } from "../../../../services/leave/leaveManagementService";
import {
  TimeOffPayloadBackend,
} from "../../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import {
  addUpdateTimeOff,
  getLeaveTypes,
} from "../../../../services/userServices/UserInfoService";
import { POLICYPERMISSIONS } from "../../../../Data/PolicyPermissions";
import { hasPolicyPermissionV2 } from "../../../../utilities/common/policyFuntions";
import CustomInput from "../../calendar/CustomInput";
import "../../formFillComponents/DynamicFormComponents/FormInput.css";
import { formatLeaveTypeLabel } from "../../../../utilities/leaveDateTime";
import "../../adminRequests/AdminRequestsDark.css";
import { LeaveImpactSummary } from "./LeaveImpactSummary";

export interface SubmittedTimeOffRequest {
  timeOffStartDate: string;
  timeOffEndDate: string;
  typeOfLeave: string;
  comment?: string;
  requestType: "FULL_DAY" | "PARTIAL_DAY";
}

interface AdminRequestModalProps {
  show: boolean;
  handleClose: () => void;
  onTimeOffSubmitted?: (request: SubmittedTimeOffRequest) => void;
}

interface SelectOption<T extends string | number> {
  value: T;
  label: string;
}

interface FormErrors {
  typeOfLeave?: string;
  startDate?: string;
  endDate?: string;
}

const TIME_OPTIONS: SelectOption<string>[] = Array.from(
  { length: 48 },
  (_, index) => {
    const hours = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? 0 : 30;
    const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    return { value, label: legacyLocalLabel((parseLocalPattern(value, "HH:mm") ?? new Date(NaN)), "h:mm a") };
  },
);

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "var(--admin-request-input, #ffffff)",
    borderColor: "var(--admin-request-border, #d1d5db)",
    boxShadow: "none",
  }),
  input: (base: any) => ({ ...base, color: "var(--admin-request-text, #111827)" }),
  singleValue: (base: any) => ({ ...base, color: "var(--admin-request-text, #111827)" }),
  placeholder: (base: any) => ({ ...base, color: "var(--admin-request-muted, #6b7280)" }),
  indicatorSeparator: (base: any) => ({ ...base, backgroundColor: "var(--admin-request-border, #d1d5db)" }),
  dropdownIndicator: (base: any) => ({ ...base, color: "var(--admin-request-muted, #6b7280)" }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: "var(--admin-request-popup, #ffffff)",
    border: "1px solid var(--admin-request-border, #e5e7eb)",
    overflow: "hidden",
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#7E57C2"
      : state.isFocused
        ? "var(--admin-request-hover, #f3f4f6)"
        : "var(--admin-request-popup, #ffffff)",
    color: state.isSelected ? "#ffffff" : "var(--admin-request-text, #111827)",
  }),
  valueContainer: (base: any) => ({ ...base, height: 40, padding: "0 12px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: 40 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 10000 }),
};

const parseLocalDate = (value: string): Date | null => {
  const parsed = (parseLocalPattern(value, "yyyy-MM-dd") ?? new Date(NaN));
  return Number.isFinite(+parsed) ? new Date(startOfDay(parsed)) : null;
};

const combineLocalDateTime = (date: string, time: string): Date | null => {
  const parsed = (parseLocalPattern(`${date} ${time}`, "yyyy-MM-dd HH:mm") ?? new Date(NaN));
  return Number.isFinite(+parsed) ? new Date(parsed) : null;
};

const formatMinutes = (minutes?: number | null): string => {
  if (minutes == null) return "--";
  const hours = minutes / 60;
  return `${hours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hr${hours === 1 ? "" : "s"}`;
};

const AdminRequestModal: React.FC<AdminRequestModalProps> = ({
  show,
  handleClose,
  onTimeOffSubmitted,
}) => {
  const {
    currentUser,
    userAddedInRolePolicy,
    currentOrganizationRolePolicies,
  } = useAuth();

  const [leaveTypes, setLeaveTypes] = useState<UpdateLeaveRequestTypeDto[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [isLoadingLeaveTypes, setIsLoadingLeaveTypes] = useState(false);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [balancePreview, setBalancePreview] = useState<LeaveRequestBalancePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [requestType, setRequestType] = useState<"FULL_DAY" | "PARTIAL_DAY">("FULL_DAY");
  const [typeOfLeave, setTypeOfLeave] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [partialDate, setPartialDate] = useState("");
  const [partialStartTime, setPartialStartTime] = useState("");
  const [partialEndTime, setPartialEndTime] = useState("");
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  const canCreateRequest = hasPolicyPermissionV2(
    currentOrganizationRolePolicies,
    POLICYPERMISSIONS.AdminRequest.CreateTimeOff.key,
    userAddedInRolePolicy,
  );

  const leaveTypeOptions = useMemo<SelectOption<number>[]>(
    () =>
      leaveTypes
        .filter((type: any) => type.isActive && !type.isDeleted)
        .map((type) => ({ value: type.id, label: formatLeaveTypeLabel(type.requestType) })),
    [leaveTypes],
  );

  const selectedBalance = leaveBalances.find(
    (balance) => balance.id === typeOfLeave,
  );

  const partialStartDateTime = useMemo(() => combineLocalDateTime(partialDate, partialStartTime), [partialDate, partialStartTime]);
  const partialEndDateTime = useMemo(() => combineLocalDateTime(partialDate, partialEndTime), [partialDate, partialEndTime]);
  const partialDurationMinutes =
    partialStartDateTime && partialEndDateTime
      ? Math.max(
          0,
          Math.round(
            (partialEndDateTime.getTime() - partialStartDateTime.getTime()) /
              60000,
          ),
        )
      : 0;

  const retrospectiveDate = requestType === "FULL_DAY" ? startDate : partialDate;
  const isRetrospective = Boolean(
    retrospectiveDate && (+(parseLocalPattern(retrospectiveDate, "yyyy-MM-dd") ?? new Date(NaN)) < +startOfDay(new Date())),
  );

  const resetForm = () => {
    setRequestType("FULL_DAY");
    setTypeOfLeave(0);
    setStartDate("");
    setEndDate("");
    setPartialDate("");
    setPartialStartTime("");
    setPartialEndTime("");
    setComment("");
    setErrors({});
    setBalancePreview(null);
    setPreviewError(null);
  };

  useEffect(() => {
    if (!show || !currentUser) return;

    resetForm();
    let cancelled = false;

    const loadLeaveData = async () => {
      setIsLoadingLeaveTypes(true);
      setIsLoadingBalances(true);

      try {
        const token = await currentUser.getIdToken();
        const [typesResponse, balanceResponse] = await Promise.all([
          getLeaveTypes(token),
          LeaveManagementService.employee(currentUser.uid, token),
        ]);

        if (cancelled) return;
        if (!("error" in typesResponse)) setLeaveTypes(typesResponse);
        setLeaveBalances(balanceResponse.data.balances || []);
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.message || "Leave information could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingLeaveTypes(false);
          setIsLoadingBalances(false);
        }
      }
    };

    void loadLeaveData();
    return () => {
      cancelled = true;
    };
  }, [show, currentUser]);

  useEffect(() => {
    if (!show) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSubmitting) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [show, isSubmitting, handleClose]);

  useEffect(() => {
    if (!show || !currentUser || !typeOfLeave) {
      setBalancePreview(null);
      setPreviewError(null);
      setIsLoadingPreview(false);
      return;
    }

    const previewStart = requestType === "FULL_DAY"
      ? (startDate ? `${startDate}T00:00:00.000Z` : null)
      : partialStartDateTime?.toISOString();
    const previewEnd = requestType === "FULL_DAY"
      ? (endDate ? `${endDate}T23:59:59.999Z` : null)
      : partialEndDateTime?.toISOString();
    if (!previewStart || !previewEnd || previewEnd < previewStart || (requestType === "PARTIAL_DAY" && partialDurationMinutes < 30)) {
      setBalancePreview(null);
      setPreviewError(null);
      setIsLoadingPreview(false);
      return;
    }

    let cancelled = false;
    setBalancePreview(null);
    setPreviewError(null);
    setIsLoadingPreview(true);
    const timer = window.setTimeout(async () => {
      try {
        const token = await currentUser.getIdToken();
        const response = await LeaveManagementService.previewRequest({
          employeeId: currentUser.uid,
          leaveTypeId: typeOfLeave,
          startTime: previewStart,
          endTime: previewEnd,
          requestType,
        }, token);
        if (!cancelled) {
          setBalancePreview(response.data);
          setPreviewError(null);
        }
      } catch (error: any) {
        if (!cancelled) {
          setBalancePreview(null);
          setPreviewError(error?.response?.data?.message || "We could not verify requestable hours for these dates. Please adjust the dates or try again.");
        }
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [show, currentUser, typeOfLeave, requestType, startDate, endDate, partialStartDateTime, partialEndDateTime, partialDurationMinutes]);

  const localSelectionIssue = requestType === "FULL_DAY" && startDate && endDate && endDate < startDate
    ? "End date cannot be before the start date."
    : requestType === "PARTIAL_DAY" && partialDate && partialStartTime && partialEndTime && partialDurationMinutes < 30
      ? "End time must be at least 30 minutes after the start time."
      : requestType === "PARTIAL_DAY" && partialDurationMinutes > 480
        ? "Partial-day requests cannot exceed 8 hours."
        : null;
  const hasNoRequestableHours = balancePreview?.isRequestable === false || balancePreview?.requestedMinutes === 0;
  const previewBlockingReason = localSelectionIssue || (hasNoRequestableHours
    ? balancePreview?.blockingReason || "The selected dates do not contain any hours that can be requested."
    : previewError);
  const previewBlockingTitle = hasNoRequestableHours ? "No requestable hours" : "Unable to submit request";
  const isPreviewReady = Boolean(
    balancePreview &&
    balancePreview.isRequestable !== false &&
    balancePreview.requestedMinutes > 0,
  );

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!typeOfLeave) nextErrors.typeOfLeave = "Select a leave type.";

    if (requestType === "FULL_DAY") {
      const start = parseLocalDate(startDate);
      const end = parseLocalDate(endDate);
      if (!start) nextErrors.startDate = "Select a start date.";
      if (!end) nextErrors.endDate = "Select an end date.";
      if (start && end && start > end) {
        nextErrors.endDate = "End date cannot be before the start date.";
      }
    } else {
      if (!partialDate || !partialStartTime) {
        nextErrors.startDate = "Select a date and start time.";
      }
      if (!partialEndTime) {
        nextErrors.endDate = "Select an end time.";
      } else if (partialDurationMinutes < 30) {
        nextErrors.endDate = "End time must be at least 30 minutes after the start time.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser || !validate() || !canCreateRequest || !isPreviewReady) return;

    setIsSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      const timeOffStartTime =
        requestType === "FULL_DAY"
          ? `${startDate}T00:00:00.000Z`
          : partialStartDateTime!.toISOString();
      const timeOffEndTime =
        requestType === "FULL_DAY"
          ? `${endDate}T23:59:59.999Z`
          : partialEndDateTime!.toISOString();

      const payload: TimeOffPayloadBackend = {
        id: 0,
        userId: currentUser.uid,
        typeOfLeave,
        comment,
        isApproved: false,
        timeOffStartTime,
        timeOffEndTime,
        requestType,
      };

      const response = await addUpdateTimeOff(payload, token);
      if (!response.success) {
        toast.error(response.message || "Leave request could not be submitted.");
        return;
      }

      const selectedLeaveType = leaveTypes.find(
        (type) => Number(type.id) === typeOfLeave,
      );
      onTimeOffSubmitted?.({
        timeOffStartDate: timeOffStartTime,
        timeOffEndDate: timeOffEndTime,
        typeOfLeave: selectedLeaveType?.requestType || "Time Off",
        comment,
        requestType,
      });

      toast.success(response.message || "Leave request submitted.");
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || "Leave request could not be submitted.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  const isFormComplete =
    typeOfLeave > 0 &&
    (requestType === "FULL_DAY"
      ? Boolean(startDate && endDate)
      : Boolean(partialDate && partialStartTime && partialEndTime));

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="time-off-request-title"
    >
      <div className="time-off-request-modal dark-popup mx-auto flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl bg-white dark:bg-zinc-900 shadow-2xl ring-1 ring-black/5 dark:ring-white/5 animate-in zoom-in-95 duration-200">
        <header className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="time-off-header-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E8FF] text-[#7E57C2]">
              <FileKey size={21} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <h2 id="time-off-request-title" className="m-0 truncate text-lg font-semibold text-gray-950 dark:text-zinc-100">
                Request time off
              </h2>
              <p className="m-0 mt-0.5 text-xs font-medium text-gray-500">
                Submit leave dates for approval
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close leave request"
            className="m-0 flex h-9 w-9 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            <X size={19} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="theme-scrollbar min-h-0 overflow-y-auto">
          <div className="space-y-4 px-5 py-4">
            <div>
              <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                Type of leave <span className="text-red-500">*</span>
              </label>
              <Select
                options={leaveTypeOptions}
                value={leaveTypeOptions.find((option) => option.value === typeOfLeave) || null}
                onChange={(option) => {
                  setTypeOfLeave(option?.value || 0);
                  setErrors((previous) => ({ ...previous, typeOfLeave: undefined }));
                }}
                placeholder="Choose a leave type"
                isLoading={isLoadingLeaveTypes}
                isDisabled={isLoadingLeaveTypes}
                className="form-v2-react-select react-select-container text-sm"
                classNamePrefix="react-select"
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPosition="fixed"
                styles={selectStyles}
              />
              {errors.typeOfLeave && (
                <p className="mb-0 ml-1 mt-1 text-[11px] font-medium text-red-500">
                  {errors.typeOfLeave}
                </p>
              )}
            </div>

            {isLoadingBalances || isLoadingPreview ? (
              <div className="time-off-preview-skeleton h-14 animate-pulse rounded-xl bg-gray-100" role="status" aria-label="Loading leave preview" />
            ) : selectedBalance ? (
              <div className="time-off-balance-card rounded-xl border border-purple-100 bg-[#FAF8FF] px-3.5 py-2.5">
                {selectedBalance.tracksBalance ? (
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <div>
                      <p className="m-0 text-[10px] font-semibold uppercase tracking-widest text-gray-400">Before</p>
                      <p className="m-0 mt-0.5 text-sm font-semibold text-gray-800">
                        {formatMinutes(balancePreview?.beforeMinutes ?? selectedBalance.availableMinutes)}
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-[#A78BCA]" />
                    <div className="text-right">
                      <p className="m-0 text-[10px] font-semibold uppercase tracking-widest text-gray-400">After request</p>
                      <p className={`m-0 mt-0.5 text-sm font-semibold ${(balancePreview?.afterMinutes ?? selectedBalance.availableMinutes) < 0 ? "text-red-600" : "text-[#6847A3]"}`}>
                        {formatMinutes(balancePreview?.afterMinutes ?? selectedBalance.availableMinutes)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="m-0 text-sm font-semibold text-[#6847A3]">No balance required for this leave type</p>
                )}
                {balancePreview && (
                  <p className="m-0 mt-1.5 text-center text-[10px] font-medium text-gray-500">
                    {formatMinutes(balancePreview.requestedMinutes)} requested
                  </p>
                )}
              </div>
            ) : null}

            <fieldset>
              <legend className="mb-1.5 ml-1 text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                Duration <span className="text-red-500">*</span>
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {(["FULL_DAY", "PARTIAL_DAY"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setRequestType(value);
                      setErrors({});
                    }}
                    className={`m-0 flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all ${
                      requestType === value
                        ? "dark-border-brand-subtle border-[#A78BCA] bg-[#FAF8FF] dark:bg-purple-400/15 text-[#6847A3] dark:text-purple-300 ring-2 ring-[#7E57C2]/10 dark:ring-purple-400/10"
                        : "dark-surface-muted dark-borderless border-gray-200 bg-white dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 hover:border-gray-300 dark:hover:bg-zinc-700"
                    }`}
                  >
                    {value === "FULL_DAY" ? <Calendar size={16} /> : <Clock size={16} />}
                    {value === "FULL_DAY" ? "Full day" : "Partial day"}
                  </button>
                ))}
              </div>
            </fieldset>

            {requestType === "FULL_DAY" ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <CustomInput
                  label="Start date *"
                  name="startDate"
                  type="date"
                  icon={Calendar}
                  value={startDate}
                  error={errors.startDate}
                  onChange={(event) => {
                    setStartDate(event.target.value);
                    setErrors((previous) => ({ ...previous, startDate: undefined }));
                  }}
                />
                <CustomInput
                  label="End date *"
                  name="endDate"
                  type="date"
                  icon={Calendar}
                  value={endDate}
                  error={errors.endDate}
                  onChange={(event) => {
                    setEndDate(event.target.value);
                    setErrors((previous) => ({ ...previous, endDate: undefined }));
                  }}
                />
              </div>
            ) : (
              <div className="dark-surface-muted dark-borderless space-y-3 rounded-2xl border border-gray-100 bg-gray-50/60 dark:bg-zinc-800/70 p-3.5">
                <CustomInput
                  label="Date *"
                  name="partialDate"
                  type="date"
                  icon={Calendar}
                  value={partialDate}
                  error={errors.startDate}
                  onChange={(event) => {
                    setPartialDate(event.target.value);
                    setErrors((previous) => ({ ...previous, startDate: undefined }));
                  }}
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                      Start time <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={TIME_OPTIONS}
                      value={TIME_OPTIONS.find((option) => option.value === partialStartTime) || null}
                      onChange={(option) => {
                        setPartialStartTime(option?.value || "");
                        setErrors((previous) => ({ ...previous, startDate: undefined }));
                      }}
                      placeholder="Select time"
                      className="form-v2-react-select react-select-container text-sm"
                      classNamePrefix="react-select"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      styles={selectStyles}
                    />
                    {errors.startDate && (
                      <p className="mb-0 ml-1 mt-1 text-[11px] font-medium text-red-500">
                        {errors.startDate}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                      End time <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={TIME_OPTIONS}
                      value={TIME_OPTIONS.find((option) => option.value === partialEndTime) || null}
                      onChange={(option) => {
                        setPartialEndTime(option?.value || "");
                        setErrors((previous) => ({ ...previous, endDate: undefined }));
                      }}
                      placeholder="Select time"
                      className="form-v2-react-select react-select-container text-sm"
                      classNamePrefix="react-select"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      styles={selectStyles}
                    />
                    {errors.endDate && (
                      <p className="mb-0 ml-1 mt-1 text-[11px] font-medium text-red-500">
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </div>
                {partialDurationMinutes >= 30 && (
                  <div className="flex items-center gap-2 text-xs font-medium text-[#6847A3]">
                    <Clock size={14} />
                    {partialDurationMinutes / 60} hour{partialDurationMinutes === 60 ? "" : "s"} requested
                  </div>
                )}
              </div>
            )}

            {isRetrospective && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs font-medium leading-relaxed text-amber-800">
                This is a retrospective leave request. It will be recorded for a past date and still requires approval.
              </div>
            )}

            {previewBlockingReason && (
              <div
                id="time-off-preview-blocker"
                role="status"
                className="time-off-preview-blocker flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs font-medium leading-relaxed text-amber-900 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200"
              >
                <AlertCircle size={17} className="mt-0.5 shrink-0" />
                <div>
                  <p className="m-0 font-semibold">{previewBlockingTitle}</p>
                  <p className="m-0 mt-0.5">{previewBlockingReason}</p>
                </div>
              </div>
            )}

            {isPreviewReady && balancePreview?.intelligence && <LeaveImpactSummary review={balancePreview.intelligence} />}

            {isPreviewReady && balancePreview?.intelligence && <p className="m-0 text-[13px] leading-5 text-slate-600 dark:text-zinc-300">
              This impact summary is advisory. Your approver can review it and decide whether to approve the request.
            </p>}

            <div>
              <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                Note for approver <span className="font-medium normal-case tracking-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                name="comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Add any helpful context."
                rows={2}
                className="dark-input-borderless block w-full resize-none rounded-xl border border-gray-200 bg-gray-50/50 dark:bg-zinc-800 px-3.5 py-2.5 text-sm text-gray-700 dark:text-zinc-200 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-zinc-500 hover:border-gray-300 focus:border-[#7E57C2] focus:bg-white dark:focus:bg-zinc-800 focus:ring-4 focus:ring-[#7E57C2]/10"
              />
            </div>
          </div>

          <footer className="dark-surface flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 dark:border-white/5 bg-white dark:bg-zinc-900 px-5 py-3.5">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="dark-surface-muted dark-borderless m-0 rounded-xl border border-gray-200 bg-white dark:bg-zinc-800 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-zinc-200 transition-colors hover:bg-gray-50 dark:hover:bg-zinc-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoadingPreview || !canCreateRequest || !isFormComplete || !isPreviewReady}
              aria-describedby={previewBlockingReason ? "time-off-preview-blocker" : undefined}
              className="m-0 inline-flex min-w-[132px] items-center justify-center gap-2 rounded-xl border-0 bg-[#7E57C2] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#6C48A8] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {hasNoRequestableHours ? "No hours to request" : previewBlockingReason ? "Cannot submit" : "Submit request"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default AdminRequestModal;
